import { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from 'recharts';
import { reqGET } from "../../Utils";
import { Link } from "react-router-dom";
import Hosts from "../../Hosts";
import { getTokenInfo } from "../../UseToken";
import { language } from '../WEBContent/Language';
import { format, parseISO } from 'date-fns';
import { color } from '../WEBContent/Color';

export default function GraficoBarrasPrioridadeTarefas() {
    const [lang, setLang] = useState('pt');
    const [isLoading, setLoading] = useState(true);    
    const [tasks, setTasks] = useState([]);
    const [taskCounts, setTaskCounts] = useState([]);

    const getCurrentSemester = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth() + 1;

        if (month >= 1 && month <= 6) {
            return `1º Semestre ${year}`;
        } else {
            return `2º Semestre ${year}`;
        }
    };
    
    const [selectedFilter, setSelectedFilter] = useState(getCurrentSemester());
    const filters = ["1º Semestre 2024", "2º Semestre 2024", "1º Semestre 2025", "2º Semestre 2025", "1º Semestre 2026", "2º Semestre 2026"];

    const monthGroups = {
        "1º Semestre 2024": ["2024-01", "2024-02", "2024-03", "2024-04", "2024-05", "2024-06"],
        "2º Semestre 2024": ["2024-07", "2024-08", "2024-09", "2024-10", "2024-11", "2024-12"],
        "1º Semestre 2025": ["2025-01", "2025-02", "2025-03", "2025-04", "2025-05", "2025-06"],
        "2º Semestre 2025": ["2025-07", "2025-08", "2025-09", "2025-10", "2025-11", "2025-12"],
        "1º Semestre 2026": ["2026-01", "2026-02", "2026-03", "2026-04", "2026-05", "2026-06"],
        "2º Semestre 2026": ["2026-07", "2026-08", "2026-09", "2026-10", "2026-11", "2026-12"],
    };

    const translations = {
        pt: {
            "January": "Janeiro", "February": "Fevereiro", "March": "Março", "April": "Abril",
            "May": "Maio", "June": "Junho", "July": "Julho", "August": "Agosto",
            "September": "Setembro", "October": "Outubro", "November": "Novembro", "December": "Dezembro",
        },
        en: {
            "Janeiro": "January", "Fevereiro": "February", "Março": "March", "Abril": "April",
            "Maio": "May", "Junho": "June", "Julho": "July", "Agosto": "August",
            "Setembro": "September", "Outubro": "October", "Novembro": "November", "Dezembro": "December",
        },
    };

    useEffect(() => {
        const fetchTasks = async () => {
            try {
                const response = await reqGET(`${Hosts.SIMCore_API}/tarefa/${getTokenInfo().companycode}`);
                if (response?.data) {
                    setTasks(response.data);
                    processTaskData(response.data, selectedFilter);
                }
            } catch (error) {
                console.error("Erro ao buscar as tasks:", error);
            }
        };
        fetchTasks();
    }, []);

    useEffect(() => {
        if (tasks.length > 0) {
            processTaskData(tasks, selectedFilter);
        }
    }, [tasks, selectedFilter]);

    const processTaskData = (tasks, filter) => {
        const filteredMonths = monthGroups[filter] || [];
        const taskCountsMap = {};

        tasks.forEach(task => {
            if (task.info?.dataConclusao) {
                const monthYear = format(parseISO(task.info.dataConclusao), "yyyy-MM");
                if (filteredMonths.includes(monthYear)) {
                    taskCountsMap[monthYear] = (taskCountsMap[monthYear] || 0) + 1;
                }
            }
        });

        const formattedData = filteredMonths.map((month, index) => {
            const monthName = format(parseISO(month + "-01"), "MMMM");
            const translatedMonth = translations[lang]?.[monthName] || monthName;
            return {
                name: month,
                displayName: translatedMonth,
                value: taskCountsMap[month] || 0,
                key: `${month}-${index}`,
            };
        });

        setTaskCounts(formattedData);
        setLoading(false);
    };

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const entry = payload[0].payload;
            const [year, month] = entry.name.split("-");

            return (
                <div
                    style={{
                        border: `1px solid ${color.corVerde.hexa2}`,
                        backgroundColor: "#fff",
                        padding: "8px",
                        borderRadius: "5px",
                        boxShadow: "0px 2px 5px rgba(0,0,0,0.2)",
                        fontSize: "14px",
                        textAlign: "start",
                        display: "flex",
                        flexDirection: "column"
                    }}
                >
                    <p style={{ margin: 0, fontWeight: "bold" }}>
                        <span>{entry.displayName}</span> <span>{year}</span>
                    </p>
                    <p style={{ margin: 0 }}>
                        <span>{language[lang].task_finished}</span> : <span>{entry.value}</span>
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="task-priority-analysis text-center">
            <div className="d-flex align-items-center w-100 position-relative" style={{ marginBottom: '5%' }}>
                <div className="position-absolute start-50 translate-middle-x">
                    <label className="fw-bold mb-0">
                        {language[lang].task_finished} - {language[lang].coletivo} 📈
                    </label>
                </div>

                <div className="ms-auto position-relative">
                    <button
                        className="btn btn-sm d-flex align-items-center custom-dropdown"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        style={{
                            backgroundColor: color.cor3.hexa,
                            color: color.corCinza.rgb3,
                            border: "none",
                            borderRadius: "8px",
                            padding: "6px 12px",
                            fontWeight: "500",
                            boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.12)",
                            transition: "all 0.3s ease",
                        }}
                    >
                        {selectedFilter}
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end custom-dropdown-menu">
                        {filters.map((filter) => (
                            <li key={filter}>
                                <button
                                    className={`dropdown-item custom-dropdown-item ${selectedFilter === filter ? "selected" : ""}`}
                                    onClick={() => setSelectedFilter(filter)}
                                    style={{
                                        padding: "8px 14px",
                                        fontSize: "13px",
                                        borderRadius: "6px",
                                        transition: "all 0.3s ease",
                                        border: "none",
                                    }}
                                >
                                    {filter}
                                </button>
                            </li>
                        ))}
                    </ul>
                    <style>
                        {`
                .custom-dropdown-menu {
                    border-radius: 10px;
                    overflow: hidden;
                    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
                    padding: 4px 0;
                    border: none;
                }

                .custom-dropdown-item {
                    transition: background-color 0.3s ease, transform 0.2s;
                    cursor: pointer;
                    border: none !important;
                }

                .custom-dropdown-item:hover {
                    background-color: #f5f5f5 !important;
                    transform: translateX(4px);
                }

                .custom-dropdown-item.selected {
                    font-weight: bold;
                    color: ${color.corVerde.hexa2};
                    background-color: rgba(0, 128, 0, 0.1);
                }
                `}
                    </style>
                </div>
            </div>

            {taskCounts.length > 0 ? (
                <Link
                    to={{ pathname: 'intranet_tarefa_list' }}
                    replace
                    style={{ display: 'block', cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}
                >
                    <ResponsiveContainer width={575} height={300}>
                        <LineChart data={taskCounts} margin={{ top: 10, right: 10, bottom: 10, left: -20 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="displayName" tick={{ fontSize: 14 }} />
                            <YAxis allowDecimals={false} domain={[1, 'auto']} />
                            <Tooltip content={<CustomTooltip />} />
                            <Line type="monotone" dataKey="value" stroke={color.corVerde.hexa2} strokeWidth={3} />
                        </LineChart>
                    </ResponsiveContainer>
                </Link>
            ) : (
                <p>{lang === "en" ? "No tasks found" : "Nenhuma tarefa encontrada"}</p>
            )}
        </div>
    );
}